import { api } from './index';

import type {
  momentsBearerQuery as momentsBearerQueryType,
  MomentsQueryError,
} from './momentsApi';
import type {
  BookMomentsSelectedPairAppointmentRequest,
  CancelMomentsSelectedPairAppointmentRequest,
  MomentsCoachWideAppointmentPage,
  MomentsCoachWideAppointmentsRequest,
  MomentsSelectedPairAppointment,
  MomentsSelectedPairAppointmentPage,
  MomentsSelectedPairAppointmentRequest,
  MomentsSelectedPairAppointmentsRequest,
  MomentsSelectedPairCancellationEvidencePage,
  MomentsSelectedPairCancellationEvidenceRequest,
  MomentsSelectedPairMutationResult,
  MomentsSelectedPairOperationPage,
  MomentsSelectedPairOperationRequest,
  MomentsSelectedPairOperationsRequest,
  MomentsSelectedPairOperationStatus,
  MomentsSelectedPairRescheduleSchedulingContext,
  MomentsSelectedPairRescheduleSchedulingContextRequest,
  MomentsSelectedPairSchedulingContext,
  MomentsSelectedPairSchedulingContextRequest,
  MomentsSelectedPairSelector,
  RescheduleMomentsSelectedPairAppointmentRequest,
  ReviewMomentsSelectedPairAppointmentRescheduleRequest,
} from '@/features/appointments/momentsSelectedPairContract';
import type { CoachAppointmentRescheduleReview } from '@/features/appointments/types';

import { appointmentContractV2 } from '@/features/appointments/contractCodec';
import {
  buildCoachWideQueryString,
  buildSelectedPairQueryString,
  matchesSelectedPairContext,
  momentsSelectedPairContract,
  validateSelectedPairRescheduleContextRequest,
} from '@/features/appointments/momentsSelectedPairContract';
import { sameInstant } from '@/features/appointments/sameInstant';

const PLATFORM_ADMIN_CALENDAR_BASE = '/api/v1/platform/admin-calendar';
const MOMENTS_SELECTED_PAIR_PAGE_LIMIT = 100;
const MAX_MOMENTS_SELECTED_PAIR_PAGE_REQUESTS = 10;

const selectedPairMatches = (
  page: MomentsSelectedPairAppointmentPage,
  request: MomentsSelectedPairAppointmentsRequest,
): boolean =>
  page.appointments.every(
    (appointment) =>
      appointment.coach.id === request.coach.externalId &&
      appointment.member.id === request.member.externalId,
  );

const coachWidePath = (
  route: string,
  request: Parameters<typeof buildCoachWideQueryString>[0],
): string => {
  const query = buildCoachWideQueryString(request);
  return `${PLATFORM_ADMIN_CALENDAR_BASE}${route}${query ? `?${query}` : ''}`;
};

const selectedPairPath = (
  route: string,
  request: Parameters<typeof buildSelectedPairQueryString>[0],
): string => {
  const query = buildSelectedPairQueryString(request);
  return `${PLATFORM_ADMIN_CALENDAR_BASE}${route}${query ? `?${query}` : ''}`;
};

const selectedPairOnlyPath = (
  route: string,
  request: MomentsSelectedPairSelector,
): string =>
  selectedPairPath(route, {
    coach: request.coach,
    member: request.member,
  });

const bookBody = (request: BookMomentsSelectedPairAppointmentRequest) => ({
  ...(request.bookingPattern
    ? { booking_pattern: { type: request.bookingPattern.type } }
    : {}),
  calendar_conflict_override: request.calendarConflictOverride ?? false,
  contract_version: 'appointment-platform-selected-pair-book.v1',
  idempotency_key: request.idempotencyKey,
  kind: request.kind,
  recurrence:
    request.recurrence === null
      ? null
      : {
          appointment_count: request.recurrence.appointmentCount,
          cadence_weeks: request.recurrence.cadenceWeeks,
        },
  seat_type: request.seatType,
  start_at: request.startAt,
  viewer_timezone: request.viewerTimezone,
});

const reviewBody = (
  request: ReviewMomentsSelectedPairAppointmentRescheduleRequest,
) => ({
  contract_version: 'appointment-platform-selected-pair-reschedule-review.v2',
  expected_series_mutation_version: request.expectedSeriesMutationVersion,
  expected_version: request.expectedVersion,
  scope: request.scope,
  series_id: request.seriesId,
  start_at: request.startAt,
  viewer_timezone: request.viewerTimezone,
});

const rescheduleBody = (
  request: RescheduleMomentsSelectedPairAppointmentRequest,
) => ({
  ...reviewBody(request),
  busy_conflicts_acknowledged: request.busyConflictsAcknowledged,
  contract_version: 'appointment-platform-selected-pair-reschedule.v2',
  idempotency_key: request.idempotencyKey,
  near_term_acknowledged: request.nearTermAcknowledged,
  review_digest: request.reviewDigest,
});

const cancelBody = (request: CancelMomentsSelectedPairAppointmentRequest) => ({
  contract_version: 'appointment-platform-selected-pair-cancel.v1',
  expected_series_mutation_version: request.expectedSeriesMutationVersion,
  expected_version: request.expectedVersion,
  idempotency_key: request.idempotencyKey,
  initiator: request.initiator,
  near_term_chemistry_confirmed: request.nearTermChemistryConfirmed,
  ops_note: request.opsNote,
  reason: request.reason,
  scope: request.scope,
});

const momentsBearerRequest = async <T>(
  ...args: Parameters<typeof momentsBearerQueryType<T>>
): Promise<ReturnType<typeof momentsBearerQueryType<T>>> => {
  const { momentsBearerQuery } = await import('./momentsApi');
  return momentsBearerQuery<T>(...args);
};

type MomentsSelectedPairCacheTag =
  'Session' | { id: string; type: 'Appointment' };

const momentsCancellationEvidenceTag = {
  id: 'MOMENTS_PLATFORM_ADMIN_CANCELLATION_EVIDENCE',
  type: 'Appointment',
} as const;

const momentsTerminalInvalidationTags = (
  result?:
    MomentsSelectedPairMutationResult | MomentsSelectedPairOperationStatus,
  argumentAppointmentIds: string[] = [],
): MomentsSelectedPairCacheTag[] => {
  if (!result || result.status === 'pending') return [];
  const appointmentIds = new Set([
    ...argumentAppointmentIds,
    ...result.appointments.map(({ id }) => id),
  ]);
  const appointmentTags = Array.from(appointmentIds, (id) => ({
    id,
    type: 'Appointment' as const,
  }));
  return [
    { id: 'MOMENTS_PLATFORM_ADMIN_LIST', type: 'Appointment' },
    { id: 'MOMENTS_PLATFORM_ADMIN_SCHEDULING_CONTEXT', type: 'Appointment' },
    ...appointmentTags,
    ...(result.status === 'confirmed' ? (['Session'] as const) : []),
  ];
};

const pageLimit = (requested?: number): number =>
  Math.min(
    requested ?? MOMENTS_SELECTED_PAIR_PAGE_LIMIT,
    MOMENTS_SELECTED_PAIR_PAGE_LIMIT,
  );

const mergeSelectedPairPages = (
  pages: MomentsSelectedPairAppointmentPage[],
): MomentsSelectedPairAppointmentPage => ({
  appointments: pages.flatMap((page) => page.appointments),
  contractVersion: 'appointment-platform-selected-pair-page.v1',
  nextCursor: pages.at(-1)?.nextCursor ?? null,
});

const mergeCoachWidePages = (
  pages: MomentsCoachWideAppointmentPage[],
): MomentsCoachWideAppointmentPage => ({
  appointments: pages.flatMap((page) => page.appointments),
  contractVersion: 'platform-admin-appointment-page.v1',
  nextCursor: pages.at(-1)?.nextCursor ?? null,
});

const fetchCoachWideAppointmentPages = async (
  request: MomentsCoachWideAppointmentsRequest,
  queryApi: Parameters<
    typeof momentsBearerQueryType<MomentsCoachWideAppointmentPage>
  >[0],
): Promise<
  { data: MomentsCoachWideAppointmentPage } | { error: MomentsQueryError }
> => {
  const pages: MomentsCoachWideAppointmentPage[] = [];
  let cursor = request.cursor ?? null;
  for (
    let page = 0;
    page < MAX_MOMENTS_SELECTED_PAIR_PAGE_REQUESTS;
    page += 1
  ) {
    // eslint-disable-next-line no-await-in-loop -- coach-wide pagination must advance using the previous page cursor.
    const result = await momentsBearerRequest(queryApi, {
      fallbackData: {
        appointments: [],
        contractVersion: 'platform-admin-appointment-page.v1',
        nextCursor: null,
      },
      mapResponse: (payload) =>
        momentsSelectedPairContract.coachWideAppointmentPage.decode(payload),
      path: coachWidePath('/appointments', {
        ...request,
        cursor,
        limit: pageLimit(request.limit),
      }),
      returnErrorOnFailure: true,
    });
    if ('error' in result) return result;
    pages.push(result.data);
    cursor = result.data.nextCursor ?? null;
    if (!cursor) break;
  }
  if (cursor) {
    return { error: { status: 'FETCH_ERROR' } };
  }
  return { data: mergeCoachWidePages(pages) };
};

const fetchSelectedPairAppointmentPages = async (
  request: MomentsSelectedPairAppointmentsRequest,
  queryApi: Parameters<
    typeof momentsBearerQueryType<MomentsSelectedPairAppointmentPage>
  >[0],
): Promise<
  { data: MomentsSelectedPairAppointmentPage } | { error: MomentsQueryError }
> => {
  const pages: MomentsSelectedPairAppointmentPage[] = [];
  let cursor = request.cursor ?? null;
  const requestedLimit = request.limit ?? MOMENTS_SELECTED_PAIR_PAGE_LIMIT;
  for (
    let page = 0;
    page < MAX_MOMENTS_SELECTED_PAIR_PAGE_REQUESTS;
    page += 1
  ) {
    const remaining =
      requestedLimit -
      pages.reduce((count, item) => count + item.appointments.length, 0);
    if (remaining <= 0) break;
    // eslint-disable-next-line no-await-in-loop -- selected-pair pagination must advance using the previous page cursor.
    const result = await momentsBearerRequest(queryApi, {
      fallbackData: {
        appointments: [],
        contractVersion: 'appointment-platform-selected-pair-page.v1',
        nextCursor: null,
      },
      mapResponse: (payload) =>
        momentsSelectedPairContract.appointmentPage.decode(payload),
      path: selectedPairPath('/appointments', {
        ...request,
        cursor,
        limit: Math.min(pageLimit(request.limit), remaining),
      }),
      returnErrorOnFailure: true,
    });
    if ('error' in result) return result;
    if (!selectedPairMatches(result.data, request)) {
      return { error: { status: 'FETCH_ERROR' } };
    }
    pages.push(result.data);
    cursor = result.data.nextCursor;
    if (!cursor || requestedLimit <= MOMENTS_SELECTED_PAIR_PAGE_LIMIT) break;
  }
  return { data: mergeSelectedPairPages(pages) };
};

const fallbackMutation = () => [
  () => ({ unwrap: () => Promise.resolve(undefined) }),
  { isLoading: false },
];

const fallbackQuery = () => ({
  data: undefined,
  error: undefined,
  isError: false,
  isFetching: false,
  isLoading: false,
});

const fallbackLazyQuery = () => [
  () => ({
    unsubscribe: () => undefined,
    unwrap: () => Promise.resolve(undefined),
  }),
  fallbackQuery(),
];

const fallbackMomentsAppointmentApi = {
  useBookMomentsSelectedPairAppointmentMutation: fallbackMutation,
  useCancelMomentsSelectedPairAppointmentMutation: fallbackMutation,
  useGetMomentsCoachWideAppointmentsQuery: fallbackQuery,
  useGetMomentsSelectedPairAppointmentQuery: fallbackQuery,
  useGetMomentsSelectedPairAppointmentsQuery: fallbackQuery,
  useGetMomentsSelectedPairCancellationEvidenceQuery: fallbackQuery,
  useGetMomentsSelectedPairOperationQuery: fallbackQuery,
  useGetMomentsSelectedPairOperationsQuery: fallbackQuery,
  useGetMomentsSelectedPairSchedulingContextQuery: fallbackQuery,
  useGetMomentsSelectedPairRescheduleSchedulingContextQuery: fallbackQuery,
  useLazyGetMomentsSelectedPairAppointmentQuery: fallbackLazyQuery,
  useLazyGetMomentsSelectedPairOperationQuery: fallbackLazyQuery,
  useLazyGetMomentsSelectedPairSchedulingContextQuery: fallbackLazyQuery,
  useRescheduleMomentsSelectedPairAppointmentMutation: fallbackMutation,
  useReviewMomentsSelectedPairAppointmentRescheduleMutation: fallbackMutation,
};

const createMomentsAppointmentApi = () =>
  api.injectEndpoints({
    endpoints: (builder) => ({
      bookMomentsSelectedPairAppointment: builder.mutation<
        MomentsSelectedPairMutationResult,
        BookMomentsSelectedPairAppointmentRequest
      >({
        invalidatesTags: (result) =>
          result && result.status !== 'pending'
            ? [
                ...momentsTerminalInvalidationTags(result),
                momentsCancellationEvidenceTag,
              ]
            : [],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            body: bookBody(arg),
            fallbackData: null as unknown as MomentsSelectedPairMutationResult,
            mapResponse: (payload) =>
              momentsSelectedPairContract.mutationResult.decode(payload),
            method: 'POST',
            path: selectedPairOnlyPath('/appointments', arg),
            returnErrorOnFailure: true,
          }),
      }),

      cancelMomentsSelectedPairAppointment: builder.mutation<
        MomentsSelectedPairMutationResult,
        CancelMomentsSelectedPairAppointmentRequest
      >({
        invalidatesTags: (result, _error, arg) =>
          result
            ? [
                momentsCancellationEvidenceTag,
                ...momentsTerminalInvalidationTags(result, [arg.appointmentId]),
              ]
            : [],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            body: cancelBody(arg),
            fallbackData: null as unknown as MomentsSelectedPairMutationResult,
            mapResponse: (payload) =>
              momentsSelectedPairContract.mutationResult.decode(payload),
            method: 'POST',
            path: selectedPairOnlyPath(
              `/appointments/${encodeURIComponent(arg.appointmentId)}/cancel`,
              arg,
            ),
            returnErrorOnFailure: true,
          }),
      }),

      getMomentsSelectedPairAppointment: builder.query<
        MomentsSelectedPairAppointment,
        MomentsSelectedPairAppointmentRequest
      >({
        providesTags: (_result, _error, arg) => [
          { id: arg.appointmentId, type: 'Appointment' },
        ],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            fallbackData: null as unknown as MomentsSelectedPairAppointment,
            mapResponse: (payload) =>
              momentsSelectedPairContract.appointment.decode(payload),
            path: selectedPairOnlyPath(
              `/appointments/${encodeURIComponent(arg.appointmentId)}`,
              arg,
            ),
            returnErrorOnFailure: true,
          }),
      }),

      getMomentsCoachWideAppointments: builder.query<
        MomentsCoachWideAppointmentPage,
        MomentsCoachWideAppointmentsRequest
      >({
        providesTags: [
          { id: 'MOMENTS_PLATFORM_ADMIN_LIST', type: 'Appointment' },
        ],
        queryFn: async (arg, queryApi) =>
          fetchCoachWideAppointmentPages(arg, queryApi),
      }),

      getMomentsSelectedPairAppointments: builder.query<
        MomentsSelectedPairAppointmentPage,
        MomentsSelectedPairAppointmentsRequest
      >({
        providesTags: [
          { id: 'MOMENTS_PLATFORM_ADMIN_LIST', type: 'Appointment' },
        ],
        queryFn: async (arg, queryApi) =>
          fetchSelectedPairAppointmentPages(arg, queryApi),
      }),

      getMomentsSelectedPairCancellationEvidence: builder.query<
        MomentsSelectedPairCancellationEvidencePage,
        MomentsSelectedPairCancellationEvidenceRequest
      >({
        providesTags: [
          {
            id: 'MOMENTS_PLATFORM_ADMIN_CANCELLATION_EVIDENCE',
            type: 'Appointment',
          },
        ],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            fallbackData:
              null as unknown as MomentsSelectedPairCancellationEvidencePage,
            mapResponse: (payload) =>
              momentsSelectedPairContract.cancellationEvidencePage.decode(
                payload,
              ),
            path: selectedPairPath('/cancellation-evidence', arg),
            returnErrorOnFailure: true,
          }),
      }),

      getMomentsSelectedPairOperation: builder.query<
        MomentsSelectedPairOperationStatus,
        MomentsSelectedPairOperationRequest
      >({
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            const tags = [
              ...momentsTerminalInvalidationTags(data),
              ...(data.command === 'cancel' && data.status !== 'pending'
                ? [momentsCancellationEvidenceTag]
                : []),
            ];
            if (tags.length > 0) dispatch(api.util.invalidateTags(tags));
          } catch {
            // Polling failures and pending responses must not invalidate caches.
          }
        },
        providesTags: (_result, _error, arg) => [
          {
            id: `moments-operation:${arg.operationId}`,
            type: 'Appointment',
          },
        ],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            fallbackData: null as unknown as MomentsSelectedPairOperationStatus,
            mapResponse: (payload) =>
              momentsSelectedPairContract.operationStatus.decode(payload),
            path: selectedPairOnlyPath(
              `/appointment-operations/${encodeURIComponent(arg.operationId)}`,
              arg,
            ),
            returnErrorOnFailure: true,
          }),
      }),

      getMomentsSelectedPairOperations: builder.query<
        MomentsSelectedPairOperationPage,
        MomentsSelectedPairOperationsRequest
      >({
        forceRefetch: ({ currentArg, previousArg }) =>
          currentArg?.cursor !== previousArg?.cursor,
        merge: (currentCache, response, { arg }) => {
          if (!arg.cursor) return response;
          const seen = new Set(
            currentCache.operations.map(({ operationId }) => operationId),
          );
          return {
            ...response,
            operations: [
              ...currentCache.operations,
              ...response.operations.filter(
                ({ operationId }) => !seen.has(operationId),
              ),
            ],
          };
        },
        providesTags: ['AuthStatus'],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            fallbackData: null as unknown as MomentsSelectedPairOperationPage,
            mapResponse: (payload) =>
              momentsSelectedPairContract.operationPage.decode(payload),
            path: selectedPairPath('/appointment-operations', arg),
            returnErrorOnFailure: true,
          }),
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}:${queryArgs.coach.externalId}:${queryArgs.member.externalId}:${queryArgs.limit}`,
      }),

      getMomentsSelectedPairRescheduleSchedulingContext: builder.query<
        MomentsSelectedPairRescheduleSchedulingContext,
        MomentsSelectedPairRescheduleSchedulingContextRequest
      >({
        providesTags: [
          {
            id: 'MOMENTS_PLATFORM_ADMIN_SCHEDULING_CONTEXT',
            type: 'Appointment',
          },
        ],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            fallbackData:
              null as unknown as MomentsSelectedPairRescheduleSchedulingContext,
            mapResponse: (payload) => {
              const decoded =
                momentsSelectedPairContract.rescheduleSchedulingContext.decode(
                  payload,
                );
              validateSelectedPairRescheduleContextRequest(decoded, arg);
              return decoded;
            },
            path: selectedPairPath('/appointments/scheduling-context', {
              ...arg,
              intent: 'reschedule',
            }),
            returnErrorOnFailure: true,
          }),
      }),

      reviewMomentsSelectedPairAppointmentReschedule: builder.mutation<
        CoachAppointmentRescheduleReview,
        ReviewMomentsSelectedPairAppointmentRescheduleRequest
      >({
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            body: reviewBody(arg),
            fallbackData: null as unknown as CoachAppointmentRescheduleReview,
            mapResponse: (payload) => {
              const decoded =
                appointmentContractV2.browserRescheduleReview.decode(payload);
              if (
                decoded.seriesId !== arg.seriesId ||
                decoded.scope !== arg.scope ||
                !sameInstant(decoded.proposedSchedule.startAt, arg.startAt) ||
                decoded.proposedSchedule.timezone !==
                  decoded.originalSchedule.timezone ||
                !decoded.reviewDigest.trim()
              )
                throw new Error(
                  'Selected-pair reschedule review identity mismatch',
                );
              return decoded;
            },
            method: 'POST',
            path: selectedPairOnlyPath(
              `/appointments/${encodeURIComponent(arg.appointmentId)}/reschedule-review`,
              arg,
            ),
            returnErrorOnFailure: true,
          }),
      }),

      rescheduleMomentsSelectedPairAppointment: builder.mutation<
        MomentsSelectedPairMutationResult,
        RescheduleMomentsSelectedPairAppointmentRequest
      >({
        invalidatesTags: (result, _error, arg) =>
          momentsTerminalInvalidationTags(result, [arg.appointmentId]),
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            body: rescheduleBody(arg),
            fallbackData: null as unknown as MomentsSelectedPairMutationResult,
            mapResponse: (payload) =>
              momentsSelectedPairContract.mutationResult.decode(payload),
            method: 'POST',
            path: selectedPairOnlyPath(
              `/appointments/${encodeURIComponent(arg.appointmentId)}/reschedule`,
              arg,
            ),
            returnErrorOnFailure: true,
          }),
      }),

      getMomentsSelectedPairSchedulingContext: builder.query<
        MomentsSelectedPairSchedulingContext,
        MomentsSelectedPairSchedulingContextRequest
      >({
        providesTags: [
          {
            id: 'MOMENTS_PLATFORM_ADMIN_SCHEDULING_CONTEXT',
            type: 'Appointment',
          },
        ],
        queryFn: async (arg, queryApi) =>
          momentsBearerRequest(queryApi, {
            fallbackData:
              null as unknown as MomentsSelectedPairSchedulingContext,
            mapResponse: (payload) => {
              const decoded =
                momentsSelectedPairContract.schedulingContext.decode(payload);
              if (
                arg.bookingWindowStart !== undefined &&
                (arg.kind !== 'coaching' ||
                  decoded.bookingWindow?.startDate !== arg.bookingWindowStart ||
                  decoded.kind !== arg.kind ||
                  decoded.timezone !== arg.viewerTimezone ||
                  !matchesSelectedPairContext(decoded, arg) ||
                  (decoded.bookingPattern?.type ?? undefined) !==
                    arg.bookingPattern)
              )
                throw new Error(
                  'Selected-pair booking window response does not match request',
                );
              return decoded;
            },
            path: selectedPairPath('/appointments/scheduling-context', arg),
            returnErrorOnFailure: true,
          }),
      }),
    }),
  });

// Some legacy tests mock the root API module with only `api.util`. Keep this
// module import-safe in those tests; production always uses the real RTK API.
export const momentsAppointmentApi =
  typeof api.injectEndpoints === 'function'
    ? createMomentsAppointmentApi()
    : (fallbackMomentsAppointmentApi as unknown as ReturnType<
        typeof createMomentsAppointmentApi
      >);

export const {
  useBookMomentsSelectedPairAppointmentMutation,
  useCancelMomentsSelectedPairAppointmentMutation,
  useGetMomentsCoachWideAppointmentsQuery,
  useGetMomentsSelectedPairAppointmentQuery,
  useGetMomentsSelectedPairAppointmentsQuery,
  useGetMomentsSelectedPairCancellationEvidenceQuery,
  useGetMomentsSelectedPairOperationQuery,
  useGetMomentsSelectedPairOperationsQuery,
  useGetMomentsSelectedPairSchedulingContextQuery,
  useGetMomentsSelectedPairRescheduleSchedulingContextQuery,
  useLazyGetMomentsSelectedPairAppointmentQuery,
  useLazyGetMomentsSelectedPairOperationQuery,
  useLazyGetMomentsSelectedPairSchedulingContextQuery,
  useRescheduleMomentsSelectedPairAppointmentMutation,
  useReviewMomentsSelectedPairAppointmentRescheduleMutation,
} = momentsAppointmentApi;
